import api from '@pray/shared/utils/api';
import type { ImportFileKind, PaymentsImportFileKind, QboImportFileKind } from './getImportDefaults';

export type PostImportCommitResponse = {
  run_id: string;
  applied: {
    added: number;
    changed: number;
    unchanged: number;
    skipped: number;
  };
  audit_count: number;
  errors: Array<{ row_index: number; message: string }>;
};

// Mirrors the server's XlsxCommitResult (QboXlsxImporter.commitImport) plus
// the file_kind the commit controller appends to the payload. Optional here
// because nothing reads it yet and existing mocks omit it.
export type QboImportCommitResponse = {
  run_id: string;
  file_kind?: QboImportFileKind;
  invoices_applied: { created: number; updated: number };
  errors: Array<{ organization_id: number | null; customer_name: string; message: string }>;
};

// Mirrors the server's PaymentsCommitResult plus the file_kind the commit
// controller appends. Optional file_kind here for the same reason as the QBO
// arm: nothing reads it yet and existing mocks may omit it.
export type PaymentsImportCommitResponse = {
  run_id: string;
  file_kind?: PaymentsImportFileKind;
  payments_applied: { created: number; skipped_duplicate: number; failed: number };
  errors: Array<{ external_payment_id: string; message: string }>;
};

export type ImportCommitResult = PostImportCommitResponse | QboImportCommitResponse | PaymentsImportCommitResponse;

export type PostImportCommitParams = {
  runId: string;
  fileKind?: ImportFileKind;
  reason?: string;
  /**
   * Per-row audit reason overrides keyed by row_index. Sent only when
   * the user filled at least one. Server validates each non-empty,
   * max 2000 chars.
   */
  rowReasons?: Record<number, string>;
};

export const postImportCommit = async ({
  runId,
  fileKind,
  reason,
  rowReasons,
}: PostImportCommitParams): Promise<ImportCommitResult> => {
  const url = '/web/studio/superfunnel/import/commit';
  const body: Record<string, unknown> = { run_id: runId };
  if (fileKind) body.file_kind = fileKind;
  if (reason) body.reason = reason;
  if (rowReasons) {
    const filtered: Record<string, string> = {};
    for (const [key, value] of Object.entries(rowReasons)) {
      const trimmed = value?.trim();
      if (trimmed) filtered[String(key)] = trimmed;
    }
    if (Object.keys(filtered).length > 0) body.row_reasons = filtered;
  }
  const response = await api.executePostRequest(url, body);
  return (response.data as { data: ImportCommitResult }).data;
};
